import db from './database';
import { getBookName, getChapterCount, OSIS_BOOK_NAMES } from '../constants/bibleConstants';

const API_BASE = 'https://api.scripture.api.bible/v1';
const API_KEY = process.env.REACT_APP_API_BIBLE_KEY;

// Available translations with their API.Bible IDs
export const TRANSLATIONS = {
  NIV: {
    id: '78a9f6124f344018-01',
    name: 'New International Version',
    abbreviation: 'NIV',
    language: 'en',
    description: 'Popular modern English translation'
  },
  NLT: {
    id: 'd8655e0e7d6f4207-01',
    name: 'New Living Translation',
    abbreviation: 'NLT',
    language: 'en',
    description: 'Thought-for-thought modern translation'
  },
  ESV: {
    id: 'f421fe261da7624f-01',
    name: 'English Standard Version',
    abbreviation: 'ESV',
    language: 'en',
    description: 'Literal modern English translation'
  },
  NKJV: {
    id: 'acde47ee76019f18-01',
    name: 'New King James Version',
    abbreviation: 'NKJV',
    language: 'en',
    description: 'Modern update of the King James Version'
  },
  KJV: {
    id: 'de4e12af7f28f599-02',
    name: 'King James Version',
    abbreviation: 'KJV',
    language: 'en',
    description: 'The classic English translation from 1611'
  },
  ASV: {
    id: '06125adad2d5898a-01',
    name: 'American Standard Version',
    abbreviation: 'ASV',
    language: 'en',
    description: 'American revision of the King James Version'
  },
  WEB: {
    id: '9879dbb7cfe39e4d-04',
    name: 'World English Bible',
    abbreviation: 'WEB',
    language: 'en',
    description: 'Modern public domain translation'
  },
  BBE: {
    id: '65eec8e0b60e656b-01',
    name: 'Bible in Basic English',
    abbreviation: 'BBE',
    language: 'en',
    description: 'Translation using basic English vocabulary'
  },
  DARBY: {
    id: '478f7eaf-e7f8-4c69-bf99-e87dfa4d4ed1',
    name: 'Darby Translation',
    abbreviation: 'DARBY',
    language: 'en',
    description: 'John Nelson Darby translation'
  },
  YLT: {
    id: '37e50a78-0a66-11ec-a1c7-0a29a8e6a413',
    name: "Young's Literal Translation",
    abbreviation: 'YLT',
    language: 'en',
    description: 'Highly literal translation by Robert Young'
  }
};

// Get headers for API requests
function getHeaders() {
  return {
    'api-key': API_KEY,
    'Content-Type': 'application/json'
  };
}

// Generic fetch with caching (currently unused but may be useful for future optimization)
// async function fetchWithCache(url, cacheKey, cacheStore) {
//   // Check cache first
//   const cached = await cacheStore.get(cacheKey);
//   if (cached) {
//     return cached;
//   }
//   
//   // Fetch from API
//   try {
//     const response = await fetch(url, { headers: getHeaders() });
//     if (!response.ok) {
//       throw new Error(`API error: ${response.status}`);
//     }
//     const data = await response.json();
//     
//     // Cache the response
//     await cacheStore.put({ ...data.data, _cacheKey: cacheKey });
//     
//     return data.data;
//   } catch (error) {
//     console.error('API fetch error:', error);
//     throw error;
//   }
// }

// Get available Bibles
export async function getBibles() {
  // If no API key is configured, return empty array (use local files instead)
  if (!API_KEY) {
    console.log('No API.Bible key configured, using local translations only');
    return [];
  }
  
  // Check cache first
  const cached = await db.bibles.toArray();
  if (cached.length > 0) {
    return cached;
  }
  
  try {
    const response = await fetch(`${API_BASE}/bibles`, { headers: getHeaders() });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    
    // Cache bibles
    await db.bibles.bulkPut(data.data);
    
    return data.data;
  } catch (error) {
    console.error('Failed to fetch bibles:', error);
    // Return our known translations as fallback
    return Object.values(TRANSLATIONS);
  }
}

// Get Bible details
export async function getBible(bibleId) {
  // If no API key, return null
  if (!API_KEY) {
    return null;
  }
  
  const cached = await db.bibles.get(bibleId);
  if (cached) return cached;
  
  try {
    const response = await fetch(`${API_BASE}/bibles/${bibleId}`, { headers: getHeaders() });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    await db.bibles.put(data.data);
    
    return data.data;
  } catch (error) {
    console.error('Failed to fetch bible:', error);
    throw error;
  }
}

// Get books for a Bible
export async function getBooks(bibleId) {
  // If no API key, return empty array
  if (!API_KEY) {
    return [];
  }
  
  // Check cache
  const cached = await db.books.where('bibleId').equals(bibleId).toArray();
  if (cached.length > 0) {
    return cached;
  }
  
  try {
    const response = await fetch(`${API_BASE}/bibles/${bibleId}/books`, { headers: getHeaders() });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    
    // Add bibleId to each book and cache
    const books = data.data.map(book => ({ ...book, bibleId }));
    await db.books.bulkPut(books);
    
    return books;
  } catch (error) {
    console.error('Failed to fetch books:', error);
    throw error;
  }
}

// Get chapters for a book
export async function getChapters(bibleId, bookId) {
  // If no API key, return empty array
  if (!API_KEY) {
    return [];
  }
  
  // Check cache
  const cached = await db.chapters
    .where('[bibleId+id]')
    .between([bibleId, ''], [bibleId, '\uffff'])
    .filter(ch => ch.bookId === bookId)
    .toArray();
  
  if (cached.length > 0) {
    return cached;
  }
  
  try {
    const response = await fetch(
      `${API_BASE}/bibles/${bibleId}/books/${bookId}/chapters`,
      { headers: getHeaders() }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    
    // Add bibleId and bookId to each chapter and cache
    const chapters = data.data.map(ch => ({ ...ch, bibleId, bookId }));
    await db.chapters.bulkPut(chapters);
    
    return chapters;
  } catch (error) {
    console.error('Failed to fetch chapters:', error);
    throw error;
  }
}

// Get chapter content with verses
export async function getChapter(bibleId, chapterId) {
  // If no API key, return null
  if (!API_KEY) {
    return null;
  }
  
  // Check cache
  const cached = await db.chapters.get([bibleId, chapterId]);
  if (cached?.content) {
    return cached;
  }
  
  try {
    const response = await fetch(
      `${API_BASE}/bibles/${bibleId}/chapters/${chapterId}?content-type=html&include-notes=false&include-titles=true&include-chapter-numbers=false&include-verse-numbers=true&include-verse-spans=true`,
      { headers: getHeaders() }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    
    // Cache the chapter with content
    await db.chapters.put({ ...data.data, bibleId });
    
    return data.data;
  } catch (error) {
    console.error('Failed to fetch chapter:', error);
    throw error;
  }
}

// Get specific verse
export async function getVerse(bibleId, verseId) {
  // If no API key, return null
  if (!API_KEY) {
    return null;
  }
  
  // Check cache
  const cached = await db.verses
    .where('[bibleId+chapterId+id]')
    .equals([bibleId, verseId.split('.').slice(0, 2).join('.'), verseId])
    .first();
  
  if (cached) {
    return cached;
  }
  
  try {
    const response = await fetch(
      `${API_BASE}/bibles/${bibleId}/verses/${verseId}?content-type=html&include-notes=false&include-titles=false&include-chapter-numbers=false&include-verse-numbers=true&include-verse-spans=true`,
      { headers: getHeaders() }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    
    // Cache the verse
    const verse = {
      ...data.data,
      bibleId,
      chapterId: verseId.split('.').slice(0, 2).join('.')
    };
    await db.verses.put(verse);
    
    return verse;
  } catch (error) {
    console.error('Failed to fetch verse:', error);
    throw error;
  }
}

// Get verse range
export async function getVerseRange(bibleId, startVerseId, endVerseId) {
  // If no API key, return null
  if (!API_KEY) {
    return null;
  }
  
  const rangeId = `${startVerseId}-${endVerseId}`;
  
  try {
    const response = await fetch(
      `${API_BASE}/bibles/${bibleId}/passages/${rangeId}?content-type=html&include-notes=false&include-titles=true&include-chapter-numbers=false&include-verse-numbers=true&include-verse-spans=true`,
      { headers: getHeaders() }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Failed to fetch verse range:', error);
    throw error;
  }
}

// Search within a Bible
export async function searchBible(bibleId, query, options = {}) {
  // If no API key, return empty results
  if (!API_KEY) {
    return { verses: [], total: 0 };
  }
  
  const { limit = 20, offset = 0, sort = 'relevance' } = options;
  
  // Log search to history
  await db.searchHistory.add({
    query,
    type: 'bible',
    createdAt: new Date().toISOString()
  });
  
  try {
    const response = await fetch(
      `${API_BASE}/bibles/${bibleId}/search?query=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}&sort=${sort}`,
      { headers: getHeaders() }
    );
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Failed to search bible:', error);
    throw error;
  }
}

// Prefetch entire book for offline use
export async function prefetchBook(bibleId, bookId) {
  const chapters = await getChapters(bibleId, bookId);
  
  const fetchPromises = chapters.map(ch => getChapter(bibleId, ch.id));
  await Promise.all(fetchPromises);
  
  return true;
}

// Prefetch entire Bible for offline use
export async function prefetchBible(bibleId, progressCallback) {
  const books = await getBooks(bibleId);
  let completed = 0;
  
  for (const book of books) {
    await prefetchBook(bibleId, book.id);
    completed++;
    if (progressCallback) {
      progressCallback(completed, books.length, book.name);
    }
  }
  
  return true;
}

// Get cross-references for a verse (if available)
export async function getCrossReferences(bibleId, verseId) {
  // Note: API.Bible doesn't directly provide cross-references
  // This would need to be supplemented with another source
  // For now, return empty array - could be extended with Blue Letter Bible API
  return [];
}

// Parse verse reference string (e.g., "John 3:16" -> { book: "JHN", chapter: 3, verse: 16 })
export function parseReference(reference) {
  const match = reference.match(/^(\d?\s?[A-Za-z]+)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  
  const [, book, chapter, startVerse, endVerse] = match;
  return {
    book: book.trim(),
    chapter: parseInt(chapter),
    startVerse: parseInt(startVerse),
    endVerse: endVerse ? parseInt(endVerse) : parseInt(startVerse)
  };
}

// Format verse ID for API (e.g., "JHN.3.16")
export function formatVerseId(bookId, chapter, verse) {
  return `${bookId}.${chapter}.${verse}`;
}

// Get book ID from name/abbreviation
export async function getBookId(bibleId, bookName) {
  const books = await getBooks(bibleId);
  const normalizedName = bookName.toLowerCase().replace(/\s+/g, '');
  
  return books.find(b => 
    b.name.toLowerCase().replace(/\s+/g, '') === normalizedName ||
    b.abbreviation.toLowerCase() === normalizedName
  )?.id;
}

// Clean HTML content to plain text
export function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

// Extract verse number from content
export function extractVerseNumber(verseSpan) {
  const match = verseSpan.match(/data-verse-id="[^"]*\.(\d+)"/);
  return match ? parseInt(match[1]) : null;
}

// ==================== Bible-API.com Integration ====================

const BIBLE_API_BASE = 'https://bible-api.com';

// Available translations on bible-api.com
export const BIBLE_API_TRANSLATIONS = {
  KJV_API: {
    id: 'bible_api_kjv',
    apiCode: 'kjv',
    name: 'King James Version',
    abbreviation: 'KJV',
    language: 'en',
    description: 'King James Version via bible-api.com',
    source: 'bible-api.com'
  },
  WEB_API: {
    id: 'bible_api_web',
    apiCode: 'web',
    name: 'World English Bible',
    abbreviation: 'WEB',
    language: 'en',
    description: 'World English Bible via bible-api.com',
    source: 'bible-api.com'
  },
  ASV_API: {
    id: 'bible_api_asv',
    apiCode: 'asv',
    name: 'American Standard Version',
    abbreviation: 'ASV',
    language: 'en',
    description: 'American Standard Version via bible-api.com',
    source: 'bible-api.com'
  },
  OEB_API: {
    id: 'bible_api_oeb',
    apiCode: 'oeb-us',
    name: 'Open English Bible',
    abbreviation: 'OEB',
    language: 'en',
    description: 'Open English Bible (US) via bible-api.com',
    source: 'bible-api.com'
  }
};

// Format reference for bible-api.com (e.g., "John 3:16" or "Genesis 1:1-3")
function formatBibleApiReference(bookName, chapter, verse) {
  // bible-api.com uses full book names with + instead of spaces
  const formattedBook = bookName.replace(/\s+/g, '+');
  const reference = verse 
    ? `${formattedBook}+${chapter}:${verse}`
    : `${formattedBook}+${chapter}`;
  return reference;
}

// Fetch verse from bible-api.com
export async function getBibleApiVerse(translationId, bookId, chapter, verse) {
  try {
    // Get translation code from translationId
    const translation = Object.values(BIBLE_API_TRANSLATIONS).find(t => t.id === translationId);
    if (!translation) {
      throw new Error(`Unknown bible-api.com translation: ${translationId}`);
    }
    
    const bookName = getBookName(bookId);
    const reference = formatBibleApiReference(bookName, chapter, verse);
    const url = `${BIBLE_API_BASE}/${reference}?translation=${translation.apiCode}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Bible API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // bible-api.com returns data in format: { reference, verses: [{ book_id, chapter, verse, text }], text, translation_id }
    if (data.verses && data.verses.length > 0) {
      const verseData = data.verses[0];
      return {
        id: `${bookId}.${chapter}.${verse}`,
        bookId,
        chapter,
        verse,
        text: verseData.text.trim(),
        reference: `${bookId}.${chapter}.${verse}`,
        bibleId: translationId
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching verse from bible-api.com:', error);
    throw error;
  }
}

// Fetch chapter from bible-api.com
export async function getBibleApiChapter(translationId, bookId, chapter) {
  try {
    const translation = Object.values(BIBLE_API_TRANSLATIONS).find(t => t.id === translationId);
    if (!translation) {
      throw new Error(`Unknown bible-api.com translation: ${translationId}`);
    }
    
    const bookName = getBookName(bookId);
    const reference = formatBibleApiReference(bookName, chapter);
    const url = `${BIBLE_API_BASE}/${reference}?translation=${translation.apiCode}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Bible API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Convert to our format
    const verses = data.verses.map(v => ({
      id: `${bookId}.${chapter}.${v.verse}`,
      bookId,
      chapter: v.chapter,
      verse: v.verse,
      text: v.text.trim(),
      reference: `${bookId}.${chapter}.${v.verse}`,
      bibleId: translationId
    }));
    
    return {
      id: `${bookId}.${chapter}`,
      bookId,
      chapter,
      verses,
      reference: data.reference,
      text: data.text,
      bibleId: translationId
    };
  } catch (error) {
    console.error('Error fetching chapter from bible-api.com:', error);
    throw error;
  }
}

// Get books list for bible-api.com translations
// Since bible-api.com doesn't have a books endpoint, we return the standard 66-book canon
export function getBibleApiBooks() {
  return Object.entries(OSIS_BOOK_NAMES).map(([id, name], index) => ({
    id,
    name,
    chapters: getChapterCount(id),
    testament: index < 39 ? 'OT' : 'NT'
  }));
}

// Wrapper for API.Bible getChapter to convert to our format
export async function getApiBibleChapter(bibleId, bookId, chapter) {
  try {
    const chapterId = `${bookId}.${chapter}`;
    const data = await getChapter(bibleId, chapterId);
    
    // If no data returned (e.g., no API key), throw error
    if (!data || !data.content) {
      throw new Error('No chapter data available - API key may be missing');
    }
    
    // Parse HTML content to extract verses
    const parser = new DOMParser();
    const doc = parser.parseFromString(data.content, 'text/html');
    const verseSpans = doc.querySelectorAll('span.verse');
    
    const verses = Array.from(verseSpans).map((span, index) => {
      const verseNum = index + 1;
      const text = span.textContent?.replace(/^\d+\s*/, '').trim() || '';
      
      return {
        id: `${bookId}.${chapter}.${verseNum}`,
        bookId,
        chapter: parseInt(chapter),
        verse: verseNum,
        text: text,
        reference: `${bookId}.${chapter}.${verseNum}`,
        bibleId
      };
    });
    
    return {
      id: chapterId,
      bookId,
      bookName: getBookName(bookId),
      chapter: parseInt(chapter),
      verses,
      bibleId,
      totalChapters: getChapterCount(bookId)
    };
  } catch (error) {
    console.error('Error fetching chapter from API.Bible:', error);
    throw error;
  }
}
